import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';

declare global {
  interface Window {
    VWO: any[];
    vwo_experiment_variation: string;
  }
}

interface PostExperiment {
  experiment: string;
  variation: string;
}

export interface LeadExperiment {
  experiment: string;
  variation: string;
  hit_segment?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ExperimentService {
  private static readonly HOPE_DIRECT_EXPERIMENT = 'Hope-Direct-AB2';
  private static readonly HOPE_DIRECT_ARMS = ['hope-direct2', 'original'];
  private static readonly CURRENT_ASSIGNMENT_PREFIX = 'fru_current_experiment_';

  demoMode: boolean;

  constructor(
    protected cookieService: CookieService
  ) {
  }

  /**
   * Hope-Direct's two-year InnoCraft cookie is not proof that this visit was
   * enrolled. The server adds one of these URL arms to both treatment and
   * control only while its live allocator handles the landing request.
   */
  static currentHopeDirectArm(search: string): string {
    const params = new URLSearchParams(search || '');
    for (const key of ['pk_ab_test', 'pk_ab_test2']) {
      const arm = params.get(key) || '';
      if (ExperimentService.HOPE_DIRECT_ARMS.indexOf(arm) >= 0) {
        return arm;
      }
    }
    return '';
  }

  /**
   * A two-year experiment_<name> cookie is sticky assignment history, not
   * evidence that this funnel entry is currently enrolled. The server writes
   * a short-lived companion only when its live allocator runs. Accept the
   * assignment when both cookies exist and carry the same arm.
   */
  static currentCookieAssignments(cookieHeader: string): Array<{
    experiment: string;
    variation: string;
    hit_segment: boolean;
  }> {
    const cookies = new Map<string, string>();
    (cookieHeader || '').split(';').forEach(part => {
      const eq = part.indexOf('=');
      if (eq < 0) { return; }
      const name = part.slice(0, eq).trim();
      if (!name) { return; }
      try {
        cookies.set(name, decodeURIComponent(part.slice(eq + 1).trim()));
      } catch (e) {
        // A malformed cookie must not break lead submission.
      }
    });

    const entries: Array<{ experiment: string; variation: string; hit_segment: boolean }> = [];
    cookies.forEach((variation, name) => {
      if (!name.startsWith('experiment_') || !variation) { return; }
      const experiment = name.slice('experiment_'.length);
      if (!experiment) { return; }
      const current = cookies.get(ExperimentService.CURRENT_ASSIGNMENT_PREFIX + experiment);
      if (current === variation) {
        entries.push({ experiment, variation, hit_segment: true });
      }
    });
    return entries;
  }

  /**
   * Preserve the generic experiment-cookie recovery from FRU-21478, but bind
   * Hope-Direct to the current server-marked entry. Without this gate, a stale
   * two-year cookie labels ordinary classic-form leads after allocation stops.
   */
  static mergeCurrentExperimentCookieTruth(
    search: string,
    existing: LeadExperiment[],
    cookieEntries: LeadExperiment[]
  ): LeadExperiment[] {
    const currentArm = ExperimentService.currentHopeDirectArm(search);
    const belongsToCurrentEntry = (entry: LeadExperiment): boolean =>
      entry.experiment !== ExperimentService.HOPE_DIRECT_EXPERIMENT
        || (!!currentArm && entry.variation === currentArm);
    const out = (Array.isArray(existing) ? existing : []).filter(belongsToCurrentEntry);
    const cookies = (Array.isArray(cookieEntries) ? cookieEntries : []).filter(belongsToCurrentEntry);
    if (!cookies.length) { return out; }
    const cookieNames = new Set(cookies.map(entry => entry.experiment));
    return out.filter(entry => !cookieNames.has(entry.experiment)).concat(cookies);
  }
  
  forPost(): PostExperiment {
    // use only for POST at the end.
    // to check whether to turn something on, please use getExperiment
    const regex = /^_vis_opt_exp_(\d*)_combi$/;
    const cookies = Object.entries(this.cookieService.getAll());
    const vwo_cookies = cookies.filter(([name, value]) => name.match(regex));
    const nothing = { experiment: '', variation: '' }

    if(vwo_cookies.length < 1) {
      return nothing;
    }
    else if(vwo_cookies.length > 1) {
      console.error('ExperimentService: more than one VWO experiment found');
      return nothing;
    } else {
      const cookie = vwo_cookies[0];
      const experiment = 'vwo_' + cookie[0].match(regex)[1]; // parse from name
      const variation = cookie[1] as string; // TS thinks '{}' but it is wrong
      return {
        experiment: experiment,
        variation: variation
      }
    }
  }

  get(): string {
    return window.vwo_experiment_variation;
  }

  enabled(variationName:string): boolean {
    return window.vwo_experiment_variation === variationName;
  }

  waitForExperiment(variationName:string): Promise<boolean> {
    const interval = 100;
    let loop;
    return new Promise((resolve, reject) => {
      loop = setInterval(() => {
        const vwoLoaded = window.VWO;
        if(vwoLoaded) {
          clearInterval(loop);
          resolve(window.vwo_experiment_variation === variationName);
        }
      }, 100);
    });
  }

}
